import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Home, LogOut, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const avatars = ['😊', '😎', '🤩', '😍', '🤓', '😎', '🥳', '🙌', '💪', '⭐', '🔥', '🎉'];

export default function Profile({ user, onLogout }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAvatar, setSelectedAvatar] = useState('😊');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [profileRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setProfile(profileRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard);
      setSelectedAvatar(profileRes.data.avatar || '😊');
    } catch (error) {
      toast.error('Profil yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="animate-pulse text-lg">Yükleniyor...</div>
      </div>
    );
  }

  const userRank = leaderboard.findIndex(l => l.username === user.username) + 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-3xl">👤</span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Profilim</h1>
              <p className="text-sm text-gray-600">{user.username}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="home-btn"
              variant="outline"
              onClick={() => navigate('/')}
              className="gap-2 hover-lift"
            >
              <Home className="w-4 h-4" />
              Ana Sayfa
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={onLogout}
              className="gap-2 hover-lift"
            >
              <LogOut className="w-4 h-4" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 shadow-lg animate-scale-in">
              <div className="flex items-center gap-6 mb-6">
                <div className="text-8xl animate-bounce-slow">{selectedAvatar}</div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-gray-900">{user.username}</h2>
                  <p className="text-gray-600">Seviye {profile.level}</p>
                  <div className="mt-3 flex gap-2">
                    {userRank && userRank <= 10 && (
                      <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold flex items-center gap-1">
                        <Crown className="w-4 h-4" />
                        #{userRank} Sıralamada
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* XP Progress */}
              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">XP</span>
                  <span className="text-sm text-gray-600">{profile.xp} / {profile.xp_for_next_level}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full progress-bar"
                    style={{ width: `${profile.xp_progress}%` }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{profile.level}</p>
                  <p className="text-sm text-gray-600">Seviye</p>
                </div>
                <div className="bg-pink-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-pink-600">{profile.total_xp}</p>
                  <p className="text-sm text-gray-600">Toplam XP</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{profile.badges?.length || 0}</p>
                  <p className="text-sm text-gray-600">Rozet</p>
                </div>
              </div>

              {/* Avatar Selection */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Avatar Seç</h3>
                <div className="grid grid-cols-6 gap-3">
                  {avatars.map((avatar) => (
                    <button
                      key={avatar}
                      onClick={() => setSelectedAvatar(avatar)}
                      className={`text-5xl p-3 rounded-xl transition-all hover-grow ${
                        selectedAvatar === avatar
                          ? 'bg-purple-100 ring-2 ring-purple-500'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      {avatar}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Leaderboard */}
          <div className="bg-white rounded-2xl p-6 shadow-lg animate-scale-in" style={{ animationDelay: '0.1s' }}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Sıralama
            </h3>
            <div className="space-y-3">
              {leaderboard.slice(0, 10).map((entry) => (
                <div
                  key={entry.rank}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                    entry.username === user.username
                      ? 'bg-purple-50 ring-2 ring-purple-300'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="text-2xl font-bold text-gray-400 w-8 text-center">
                    {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
                  </div>
                  <div className="text-3xl">{entry.avatar}</div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{entry.username}</p>
                    <p className="text-xs text-gray-600">Lv.{entry.level} • {entry.total_xp} XP</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}